<template>
  <div>
    <a-alert
      :message="$t('college.bulletin.config.title')"
      :description="$t('college.bulletin.config.description')"
      type="success"
      show-icon
      closable
    />
    <div class="row my-2">
      <div class="col-md-4">
        <a-select
          size="large"
          :value="selectedLevel"
          @change="handleLevelChange"
          class="pb-2 pt-2 w-100"
        >
          <a-select-option
            v-for="(value, name) in levelSelectData"
            :key="name"
            :value="name"
          >
            {{ value }}
          </a-select-option>
        </a-select>
      </div>
      <div class="col-md-4 row">
        <div class="col-md-3 pt-2 center">
          <label style="margin-right: 5px; margin-left: 15px">{{
            this.$t("emploi.trimestre")
          }}</label>
        </div>
        <div class="col-md-9">
          <a-select
            size="large"
            :placeholder="$t('paiement.selectionnerMois')"
            class="pb-2 p-2 w-100"
            @change="handleTrimesterChange"
            :default-value="selectedTrimester"
          >
            <a-select-option
              v-for="trimester in trimesters"
              :key="trimester"
              :value="trimester"
            >
              {{ trimester }}
            </a-select-option>
          </a-select>
        </div>
      </div>

      <div class="col-md-2 mt-1">
        <a-button
          type="primary"
          icon="copy"
          class="mx-auto my-2 w-100"
          @click="openCloneModal"
          :disabled="isReadOnly"
        >
          {{ $t("action.clone") }}
        </a-button>
      </div>

      <div class="col-md-2 mt-1">
        <a-button
          type="primary"
          icon="plus"
          class="mx-auto my-2 w-100"
          @click="showAddSubjectModal"
          :disabled="isReadOnly"
        >
          {{ $t("scolarite.ajouterMatiere") }}
        </a-button>
      </div>
    </div>

    <div class="card">
      <a-table
        :loading="tableLoading"
        :rowKey="'_id'"
        :columns="columns"
        :data-source="subjects"
        :pagination="false"
        :components="components"
      >
        <template slot="dragHandle">
          <a-icon
            type="menu"
            class="drag-handle"
            style="cursor: move; font-size: 17px"
          />
        </template>
        <template slot="subject" slot-scope="text, record">
          <span>{{ record.subject ? record.subject.name : "-" }}</span>
        </template>
        <template slot="markTypes" slot-scope="text, record">
          <div>
            <template v-for="(markType, key) in record.markTypes">
              <a-tag
                v-if="markType.enabled"
                :key="key"
                color="green"
                class="mb-1"
              >
                {{ $t(`college.bulletin.config.markType.${key}`) }}
                ({{ markType.coef }}x{{ markType.count }})
              </a-tag>
            </template>
            <span
              v-if="
                !record.markTypes ||
                !Object.keys(record.markTypes).some(
                  (key) => record.markTypes[key].enabled
                )
              "
            >
              {{ $t("scolarite.aucunMarkType") }}
            </span>
          </div>
        </template>
        <span slot="operation" slot-scope="text, record">
          <div class="editable-row-operations">
            <a-button
              type="primary"
              @click="() => editSubject(record)"
              class="mr-2"
              :disabled="isReadOnly"
            >
              <a-icon type="edit" />{{ $t("action.modifier") }}
            </a-button>
            <a-button
              type="danger"
              :loading="deletingSubjectId === record._id"
              :disabled="deletingSubjectId !== null || isReadOnly"
              @click.stop
            >
              <a-popconfirm
                :title="$t('requis.supp')"
                @confirm="() => deleteSubject(record._id)"
              >
                <a>{{ $t("action.supprimer") }}</a>
              </a-popconfirm>
            </a-button>
          </div>
        </span>
      </a-table>
    </div>

    <!-- Add Subject Modal -->
    <subject-modal
      :visible="visibleSubjectModal"
      :subject-data="subjectData"
      :available-subjects="availableSubjects"
      :mark-types-keys="markTypesKeys"
      :subsubjects="subsubjects"
      :loading="loadingSave"
      :is-edit="isEditSubject"
      :is-read-only="isReadOnly"
      @cancel="handleCancelSubjectModal"
      @submit="handleSubjectSubmit"
      @show-mark-type-settings="handleShowMarkTypeSettings"
    />

    <!-- Clone Modal -->
    <clone-modal
      :visible="visibleCloneModal"
      :source-level="String(sourceLevel || selectedLevel)"
      :source-trim="selectedTrimester"
      :target-levels="targetLevels"
      :target-trims="targetTrims"
      :level-select-data="levelSelectData"
      :trimesters="trimesters"
      :loading="loadingClone"
      :is-read-only="isReadOnly"
      @cancel="handleCancelClone"
      @submit="handleClone"
    />

    <!-- Mark Type Settings Modal -->
    <mark-type-settings-modal
      :visible="visibleMarkTypeSettingsModal"
      :mark-type="selectedMarkType"
      :subsubjects="subsubjects"
      @cancel="handleCancelMarkTypeSettings"
      @save="handleSaveMarkTypeSettings"
    />
  </div>
</template>

<script>
/* eslint-disable */
import apiClient from "@/services/axios";
import { mapState, mapGetters } from "vuex";
import SubjectModal from "./components/SubjectModal.vue";
import CloneModal from "./components/CloneModal.vue";
import MarkTypeSettingsModal from "./components/MarkTypeSettingsModal.vue";
import DraggableWrapper from "./components/DraggableWrapper/index.vue";
import { getLevelsCollegeLycee } from "@/config/LevelConstants";

export default {
  name: "bulletinCollegeConfig",
  components: {
    SubjectModal,
    CloneModal,
    MarkTypeSettingsModal,
    DraggableWrapper,
  },
  provide() {
    return {
      data: this,
    };
  },
  async created() {
    this.$gtag.pageview({
      page_path: this.$route.path,
      page_location: window.location.href,
      page_title: document.title + " - Configuration Bulletin Collège",
    });

    this.schoolType = this.activeBuilding.type;

    const collegeLyceeLevels = getLevelsCollegeLycee(this.$t.bind(this), this.countryCode);
    const availableLevels = Object.keys(collegeLyceeLevels)
      .map(Number)
      .sort((a, b) => a - b);
    const firstLevel = availableLevels.length > 0 ? String(availableLevels[0]) : null;
    this.selectedLevel = firstLevel;
    this.selectedTrimester = "1";
    this.sourceLevel = firstLevel || "";
    this.sourceTrim = this.selectedTrimester;

    this.loadConfig();
    this.loadSubjects();
    this.loadSubsubjects();
  },
  data() {
    return {
      schoolType: null,
      selectedLevel: null,
      selectedTrimester: "1",
      sourceLevel: "",
      sourceTrim: "1",
      tableLoading: false,
      currentConfig: null,
      subjects: [],
      availableSubjects: [],
      subsubjects: [],
      markTypesKeys: ["oral", "cont1", "cont2", "synth", "written", "tp"],
      trimesters: ["1", "2", "3"],
      columns: [
        {
          title: "",
          key: "dragHandle",
          width: 50,
          scopedSlots: { customRender: "dragHandle" },
        },
        {
          title: this.$t("emploi.matiere"),
          dataIndex: "subject",
          key: "subject",
          scopedSlots: { customRender: "subject" },
        },
        {
          title: this.$t("scolarite.coefficient"),
          dataIndex: "coef",
          key: "coef",
        },
        {
          title: this.$t("college.bulletin.config.markTypes"),
          dataIndex: "markTypes",
          key: "markTypes",
          scopedSlots: { customRender: "markTypes" },
        },
        {
          title: this.$t("action.actions"),
          dataIndex: "operation",
          key: "operation",
          scopedSlots: { customRender: "operation" },
          fixed: "right",
        },
      ],
      visibleSubjectModal: false,
      visibleCloneModal: false,
      visibleMarkTypeSettingsModal: false,
      loadingSave: false,
      loadingClone: false,
      deletingSubjectId: null,
      subjectData: {
        subject: null,
        coef: 1,
        order: 0,
        markTypes: {},
        status: "active",
      },
      isEditSubject: false,
      editingSubjectId: null,
      selectedMarkType: null,
      selectedMarkTypeContext: null,
      targetLevels: [],
      targetTrims: [],
      components: {
        body: {
          wrapper: DraggableWrapper,
        },
      },
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    ...mapGetters("settings", ["packDetailsGetter"]),
    isReadOnly() {
      const value = this.packDetailsGetter?.access?.edManager?.bulletinCollege;
      return value === "ro";
    },
    schoolDetails() {
      return (
        this.user.building.filter(
          (el) => el.dbName == this.settings.activeBuilding
        )[0] || {}
      );
    },
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    countryCode() {
      return this.activeBuilding?.countryCode;
    },
    levelSelectData() {
      return getLevelsCollegeLycee(this.$t.bind(this), this.countryCode);
    },
  },
  methods: {
    handleApiError(error, defaultMessage) {
      // Check for PACK_PERMISSION_ERROR
      if (
        error.response?.data?.errorCode === "PACK_PERMISSION_ERROR" ||
        error.response?.data?.error?.errorCode === "PACK_PERMISSION_ERROR"
      ) {
        this.$message.error(this.$t("errorCode.PACK_PERMISSION_ERROR"));
        return;
      }
      // Fallback to default error message
      this.$message.error(
        error.response?.data?.message || defaultMessage || this.$t("error.erreur")
      );
    },
    async loadSubjects() {
      try {
        const res = await apiClient.post("/subject/filter", {
          query: {
            status: "active",
          },
        });
        this.availableSubjects = res.data || [];
      } catch (e) {
        this.$message.error(this.$t("error.aucMatiere"));
      }
    },
    async loadSubsubjects() {
      try {
        const res = await apiClient.post("/subsubject/filter", {
          query: {
            status: "active",
          },
        });
        this.subsubjects = res.data || [];
      } catch (e) {
        // Subsubjects are optional
      }
    },
    async loadConfig() {
      this.tableLoading = true;
      try {
        const res = await apiClient.post("/bulletin/college/config/subjects", {
          level: parseInt(this.selectedLevel),
          trimester: this.selectedTrimester
            ? parseInt(this.selectedTrimester)
            : null,
        });

        if (res.data && res.data.length > 0) {
          // Store configId from first subject if available
          const configId = res.data[0].configId;
          if (configId) {
            this.currentConfig = { _id: configId };
          }
          this.subjects = res.data.map((subj) => ({
            ...subj,
            key: subj._id,
            markTypes: Object.fromEntries(
              Object.entries(subj.markTypes || {}).map(([key, markType]) => [
                key,
                {
                  ...markType,
                  subsubject:
                    markType.subsubject && markType.subsubject._id
                      ? String(markType.subsubject._id)
                      : markType.subsubject
                      ? String(markType.subsubject)
                      : null,
                },
              ])
            ),
            configId: configId, // Ensure configId is on each subject for DraggableWrapper
          }));
        } else {
          this.currentConfig = null;
          this.subjects = [];
        }
      } catch (e) {
        console.log(e);
        this.$message.error(this.$t("error.erreur"));
        this.subjects = [];
        this.currentConfig = null;
      } finally {
        this.tableLoading = false;
      }
    },
    handleLevelChange(value) {
      this.selectedLevel = String(value);
      this.loadConfig();
    },
    handleTrimesterChange(value) {
      this.selectedTrimester = value;
      this.loadConfig();
    },
    showAddSubjectModal() {
      this.isEditSubject = false;
      this.editingSubjectId = null;
      this.subjectData = {
        subject: null,
        coef: 1,
        order: this.subjects.length,
        markTypes: {},
        status: "active",
      };
      this.visibleSubjectModal = true;
    },
    editSubject(record) {
      this.isEditSubject = true;
      this.editingSubjectId = record._id;
      this.subjectData = {
        subject: record.subject ? record.subject._id || record.subject : null,
        coef: record.coef || 1,
        order: record.order || 0,
        markTypes: record.markTypes
          ? JSON.parse(JSON.stringify(record.markTypes))
          : {},
        status: record.status || "active",
      };
      this.visibleSubjectModal = true;
    },
    async deleteSubject(subjectId) {
      try {
        // Set loading state
        this.deletingSubjectId = subjectId;

        // Find the subject record to get configId and actual subject ID
        const subjectRecord = this.subjects.find(
          (s) => String(s._id) === String(subjectId)
        );
        if (!subjectRecord) {
          this.$message.error(this.$t("error.erreur"));
          this.deletingSubjectId = null;
          return;
        }

        const configId =
          subjectRecord.configId ||
          (this.currentConfig && this.currentConfig._id);
        const actualSubjectId =
          subjectRecord.subject?._id || subjectRecord.subject;

        if (!configId || !actualSubjectId) {
          this.$message.error(this.$t("error.erreur"));
          this.deletingSubjectId = null;
          return;
        }

        // First check if there are any marks for this subject
        const checkResponse = await apiClient.post(
          `/bulletin/college/config/subject/check-marks`,
          {
            configId: configId,
            subjectId: actualSubjectId,
          }
        );

        // Reset loading state before showing modal or proceeding
        this.deletingSubjectId = null;

        if (checkResponse.data.hasMarks) {
          // Show confirmation dialog if marks exist
          const marksCount = checkResponse.data.marksCount;
          this.$confirm({
            title: this.$t("all.sureToDelete"),
            content: this.$t("college.bulletin.config.confirmDeleteWithMarks", {
              count: marksCount,
            }),
            okText: this.$t("all.oui"),
            cancelText: this.$t("all.non"),
            onOk: async () => {
              this.deletingSubjectId = subjectId;
              await this.performDelete(subjectId);
              this.deletingSubjectId = null;
            },
            onCancel: () => {
              // User cancelled
            },
          });
        } else {
          // No marks, proceed with deletion directly
          this.deletingSubjectId = subjectId;
          await this.performDelete(subjectId);
          this.deletingSubjectId = null;
        }
      } catch (e) {
        this.deletingSubjectId = null;
        this.handleApiError(e, this.$t("error.erreur"));
      }
    },
    async performDelete(subjectId) {
      try {
        await apiClient.delete(`/bulletin/college/config/subject/${subjectId}`);
        this.$message.success(this.$t("success.matiereSupp"));
        await this.loadConfig();
      } catch (e) {
        this.deletingSubjectId = null;
        this.handleApiError(e, this.$t("error.erreur"));
      }
    },
    handleSubjectSubmit(payload) {
      if (this.isEditSubject && this.editingSubjectId) {
        this.updateSubject(payload);
      } else {
        this.addSubject(payload);
      }
    },
    async addSubject(payload) {
      // Frontend validation: Check if subject is provided
      if (!payload.subject) {
        this.$message.error(this.$t("requis.matiere"));
        return;
      }

      // Frontend validation: Check if subject already exists
      const subjectId = payload.subject._id || payload.subject;
      const subjectExists = this.subjects.some(
        (s) => String(s.subject._id || s.subject) === String(subjectId)
      );
      if (subjectExists) {
        this.$message.error(
          this.$t("college.bulletin.config.subjectAlreadyExists")
        );
        return;
      }

      this.loadingSave = true;
      try {
        const subjectData = {
          subject: subjectId,
          coef: payload.coef || 1,
          order: payload.order || this.subjects.length,
          markTypes: payload.markTypes || {},
          status: payload.status || "active",
        };

        await apiClient.post("/bulletin/college/config/subject", {
          level: parseInt(this.selectedLevel),
          trimester: this.selectedTrimester
            ? parseInt(this.selectedTrimester)
            : null,
          subject: subjectData,
        });

        this.$message.success(this.$t("success.matiereAjout"));
        this.visibleSubjectModal = false;
        await this.loadConfig();
      } catch (e) {
        this.handleApiError(e, this.$t("error.erreur"));
      } finally {
        this.loadingSave = false;
      }
    },
    async updateSubject(payload) {
      this.loadingSave = true;
      try {
        const subjectData = {
          subject: payload.subject,
          coef: payload.coef || 1,
          order: payload.order || 0,
          markTypes: payload.markTypes || {},
          status: payload.status || "active",
        };

        if (!this.currentConfig || !this.currentConfig._id) {
          this.$message.error(this.$t("error.erreur"));
          this.loadingSave = false;
          return;
        }

        await apiClient.patch(
          `/bulletin/college/config/subject/${this.editingSubjectId}`,
          {
            subject: subjectData,
            configId: this.currentConfig._id,
          }
        );

        this.$message.success(this.$t("success.matiereMSA"));
        this.visibleSubjectModal = false;
        this.isEditSubject = false;
        this.editingSubjectId = null;
        await this.loadConfig();
      } catch (e) {
        this.handleApiError(e, this.$t("error.erreur"));
      } finally {
        this.loadingSave = false;
      }
    },
    handleCancelSubjectModal() {
      this.visibleSubjectModal = false;
      this.isEditSubject = false;
      this.editingSubjectId = null;
      this.subjectData = {
        subject: null,
        coef: 1,
        order: 0,
        markTypes: {},
        status: "active",
      };
    },
    handleShowMarkTypeSettings(markTypeKey, markTypeData) {
      if (!this.subjectData.markTypes) {
        this.$set(this.subjectData, "markTypes", {});
      }
      if (markTypeData) {
        this.$set(this.subjectData.markTypes, markTypeKey, { ...markTypeData });
      } else if (!this.subjectData.markTypes[markTypeKey]) {
        const defaultCoef = markTypeKey === "synth" ? 2 : 1;
        this.$set(this.subjectData.markTypes, markTypeKey, {
          enabled: false,
          coef: defaultCoef,
          count: 1,
          subsubject: null,
        });
      }
      this.selectedMarkType = this.subjectData.markTypes[markTypeKey];
      this.selectedMarkTypeContext = { markTypeKey };
      this.visibleMarkTypeSettingsModal = true;
    },
    handleSaveMarkTypeSettings() {
      this.visibleMarkTypeSettingsModal = false;
      this.selectedMarkType = null;
      this.selectedMarkTypeContext = null;
    },
    handleCancelMarkTypeSettings() {
      this.visibleMarkTypeSettingsModal = false;
      this.selectedMarkType = null;
      this.selectedMarkTypeContext = null;
    },
    openCloneModal() {
      this.sourceLevel = this.selectedLevel ? String(this.selectedLevel) : "";
      this.sourceTrim = this.selectedTrimester;
      this.targetLevels = [];
      this.targetTrims = [];
      this.visibleCloneModal = true;
    },
    handleClone(payload) {
      this.loadingClone = true;
      apiClient
        .post("/bulletin/college/config/clone", payload)
        .then(() => {
          this.$message.success(this.$t("college.bulletin.config.configClone"));
          this.visibleCloneModal = false;
          this.loadConfig();
        })
        .catch((e) => {
          this.handleApiError(e, this.$t("error.erreur"));
        })
        .finally(() => {
          this.loadingClone = false;
        });
    },
    handleCancelClone() {
      this.visibleCloneModal = false;
      this.targetLevels = [];
      this.targetTrims = [];
    },
  },
};
</script>

<style scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.card >>> .ant-table-tbody tr {
  cursor: pointer !important;
}

.card >>> .ant-table-tbody tr.dragging-row {
  opacity: 0.5;
}

.center {
  display: flex;
  align-items: center;
}

.drag-handle {
  cursor: move;
}
</style>
